import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { CaretLeft } from '@phosphor-icons/react';
import { db, functions } from '@/core/firebase';
import { useAuth } from '@/core/AuthContext';
import { useIsMobile } from '../lib/useIsMobile';
import type { Comment, SetLogRecord, WorkoutSession } from '../types';
import type { SetCard } from '../lib/workoutCards';
import { useExerciseSwap } from '../hooks/useExerciseSwap';
import { useWorkoutPlayback } from '../hooks/useWorkoutPlayback';
import { CardNavigator } from '../components/workout/CardNavigator';
import { PlaylistView } from '../components/workout/PlaylistView';
import { Confetti } from '../components/workout/Confetti';
import { CompletionScreen } from '../components/workout/CompletionScreen';
import { ExerciseSwapModal } from '../components/workout/ExerciseSwapModal';
import { SessionSummary } from '../components/workout/SessionSummary';
import { schedulePostWorkoutCelebration } from '@/core/notifications';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';

/**
 * Remove undefined values from an object recursively
 * Firestore doesn't accept undefined values
 */
function removeUndefined<T>(obj: T): T {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(removeUndefined) as unknown as T;
  }

  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = removeUndefined(value);
      }
    }
    return cleaned as T;
  }

  return obj;
}

/**
 * Returns true when a string is a raw Firestore document ID rather than a
 * human-readable exercise name.  We detect two common forms:
 *   - RFC-4122 UUID  (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
 *   - Firestore auto-ID  (exactly 20 alphanumeric characters)
 */
function isRawId(value: string): boolean {
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
    return true;
  }
  if (/^[A-Za-z0-9]{20}$/.test(value)) {
    return true;
  }
  return false;
}

export function SessionView() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { user, userData } = useAuth();
  const isMobile = useIsMobile();

  const [session, setSession] = useState<WorkoutSession | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [logHistory, setLogHistory] = useState<Record<string, SetLogRecord[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [showConfetti, setShowConfetti] = useState(false);

  // Comment state
  const [commentText, setCommentText] = useState('');

  // Session-level media
  const [sessionNote, setSessionNote] = useState('');
  const [sessionRating, setSessionRating] = useState(0);

  // Cancel confirmation dialog
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  // Load session
  useEffect(() => {
    if (!sessionId) return;

    const unsub = onSnapshot(
      doc(db, 'workoutSessions', sessionId),
      (snap) => {
        if (!snap.exists()) {
          setError('Session not found');
          return;
        }
        setError(null);
        const data = { id: snap.id, ...snap.data() } as WorkoutSession;
        setSession(data);
        setSessionNote(data.sessionNote || '');
        setSessionRating(data.rating || 0);
      },
      (err) => {
        console.error('Failed to load session', err);
        setError('Unable to load this session right now.');
      }
    );

    return () => unsub();
  }, [sessionId]);

  // Resolve exercise names for snapshot items that are missing the field or
  // have a raw document ID stored in exerciseName (legacy sessions written
  // before the field was reliably denormalised).  We patch the in-memory
  // session so the card playlist renders correct human-readable names without
  // requiring a Firestore write.
  useEffect(() => {
    if (!session) return;

    const itemsNeedingResolution = session.workoutSnapshot.items.filter(
      (item) => !item.exerciseName || isRawId(item.exerciseName)
    );

    if (itemsNeedingResolution.length === 0) return;

    const resolveNames = async () => {
      // Deduplicate exerciseId values so we only fetch each exercise once
      const uniqueExerciseIds = [
        ...new Set(itemsNeedingResolution.map((item) => item.exerciseId)),
      ];

      // Fetch all exercise documents in parallel
      const exerciseNameMap = new Map<string, string>();
      await Promise.all(
        uniqueExerciseIds.map(async (exerciseId) => {
          // Skip IDs that are not real Firestore auto-IDs
          // (e.g. AI-generated sessions that used the exercise name as the ID)
          if (!exerciseId || !isRawId(exerciseId)) return;
          try {
            const snap = await getDoc(doc(db, 'exercises', exerciseId));
            if (snap.exists()) {
              const name = snap.data().name as string | undefined;
              if (name) {
                exerciseNameMap.set(exerciseId, name);
              }
            }
          } catch {
            // Non-fatal — workoutCards.ts fallback will show 'Exercise'
          }
        })
      );

      if (exerciseNameMap.size === 0) return;

      // Patch the in-memory session items with resolved names
      const patchedItems = session.workoutSnapshot.items.map((item) => {
        if (!item.exerciseName || isRawId(item.exerciseName)) {
          const resolved = exerciseNameMap.get(item.exerciseId);
          if (resolved) {
            return { ...item, exerciseName: resolved };
          }
        }
        return item;
      });

      setSession((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          workoutSnapshot: {
            ...prev.workoutSnapshot,
            items: patchedItems,
          },
        };
      });
    };

    void resolveNames();
    // We intentionally depend only on session.id so this runs once per loaded
    // session, not on every snapshot update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);

  // Load comments
  useEffect(() => {
    if (!sessionId || !session) return;

    const commentsQuery = query(
      collection(db, 'comments'),
      where('sessionId', '==', sessionId),
      where('clientId', '==', session.clientId),
      where('trainerId', '==', session.trainerId),
      orderBy('createdAt', 'desc')
    );

    const unsub = onSnapshot(commentsQuery, (snap) => {
      const docs = snap.docs.map(
        (docSnap) => ({ id: docSnap.id, ...docSnap.data() } as Comment)
      );
      setComments(docs);
    });

    return () => unsub();
  }, [sessionId, session?.clientId, session?.trainerId]);

  // Load set log history
  useEffect(() => {
    if (!session) return;

    const loadHistory = async () => {
      try {
        const entries = await Promise.all(
          session.workoutSnapshot.items.map(async (item) => {
            const historyQuery = query(
              collection(db, 'setLogs'),
              where('clientId', '==', session.clientId),
              where('trainerId', '==', session.trainerId),
              where('exerciseId', '==', item.exerciseId),
              orderBy('performedAt', 'desc'),
              limit(5)
            );
            const snap = await getDocs(historyQuery);
            const rows = snap.docs.map(
              (docSnap) => ({ id: docSnap.id, ...docSnap.data() } as SetLogRecord)
            );
            return [item.id, rows] as const;
          })
        );
        setLogHistory(Object.fromEntries(entries));
      } catch (err) {
        console.error('Failed to load history', err);
      }
    };

    void loadHistory();
  }, [session]);

  const {
    cards,
    currentCardIndex,
    setCurrentCardIndex,
    isPlaying,
    isCompletionScreen,
    playlistOpen,
    setPlaylistOpen,
    ttsEnabled,
    restTimerStates,
    setRestTimerStates,
    handleTogglePlay,
    handleToggleTts,
  } = useWorkoutPlayback({ session, logHistory });

  // Update session in Firestore
  const updateSession = async (nextSession: WorkoutSession) => {
    if (!sessionId) return;
    setSaving(true);
    setError(null);
    setSession(nextSession);

    try {
      // Remove undefined values before saving
      const cleanSnapshot = removeUndefined(nextSession.workoutSnapshot);
      const cleanMedia = removeUndefined(nextSession.progressMedia || []);

      const updateWorkoutSessionFn = httpsCallable<
        {
          sessionId: string;
          workoutSnapshot: WorkoutSession['workoutSnapshot'];
          sessionNote: string;
          progressMedia: WorkoutSession['progressMedia'];
        },
        { success: boolean; sessionId: string }
      >(functions, 'updateWorkoutSession');

      await updateWorkoutSessionFn({
        sessionId,
        workoutSnapshot: cleanSnapshot,
        sessionNote: nextSession.sessionNote || '',
        progressMedia: cleanMedia,
      });
    } catch (err) {
      console.error('Failed to update session', err);
      setError('Unable to save session changes right now.');
    } finally {
      setSaving(false);
    }
  };

  const {
    swapItem,
    swapOptions,
    swapLoading,
    swapSaving,
    swapError,
    openSwapModal,
    closeSwapModal,
    handleExerciseSwap,
  } = useExerciseSwap({ session, onUpdateSession: updateSession });

  // Handle set field updates
  const handleSetUpdate = (cardId: string, field: 'weight' | 'repsDone' | 'notes', value: string) => {
    if (!session) return;

    const card = cards.find((c) => c.id === cardId) as SetCard | undefined;
    if (!card || card.type !== 'set') return;

    const parsedValue = field === 'notes' ? value : value === '' ? undefined : Number(value);

    const updatedItems = session.workoutSnapshot.items.map((item) => {
      if (item.id !== card.itemId) return item;

      const newSets = item.sets.map((set) =>
        set.index === card.setIndex ? { ...set, [field]: parsedValue } : set
      );

      return { ...item, sets: newSets };
    });

    const updatedSession = {
      ...session,
      workoutSnapshot: { ...session.workoutSnapshot, items: updatedItems },
    };

    updateSession(updatedSession);

    // Save set log
    if (field === 'weight' || field === 'repsDone') {
      const updatedSet = updatedItems
        .find((i) => i.id === card.itemId)
        ?.sets.find((s) => s.index === card.setIndex);

      if (updatedSet) {
        void saveSetLog(card.itemId, updatedSet).catch((err) => {
          console.error('Failed to save set log', err);
          setError('Unable to save this set right now.');
        });
      }
    }
  };

  // Get user's preferred unit (default to lb)
  const preferredUnit: 'kg' | 'lb' = (userData as any)?.preferredUnit === 'kg' ? 'kg' : 'lb';

  // Save set log through a callable so ownership fields are derived server-side.
  const saveSetLog = async (
    itemId: string,
    set: { index: number; weight?: number; repsDone?: number }
  ) => {
    if (!session) return;

    const item = session.workoutSnapshot.items.find((i) => i.id === itemId);
    if (!item) return;

    const saveWorkoutSetLogFn = httpsCallable<
      {
        sessionId: string;
        workoutItemId: string;
        setIndex: number;
        weight?: number;
        repsDone?: number;
        preferredUnit: 'kg' | 'lb';
      },
      { success: boolean; sessionId: string; logId: string }
    >(functions, 'saveWorkoutSetLog');

    await saveWorkoutSetLogFn(removeUndefined({
      sessionId: session.id,
      workoutItemId: itemId,
      setIndex: set.index,
      weight: set.weight,
      repsDone: set.repsDone,
      preferredUnit,
    }));
  };

  // Complete session
  const completeSession = async () => {
    if (!sessionId || !session) return;
    setSaving(true);
    setError(null);

    try {
      // Show celebration
      setShowConfetti(true);

      console.log('[SessionView] Completing session:', sessionId);

      // Sanitize workoutSnapshot and progressMedia before saving
      const cleanSnapshot = removeUndefined(session.workoutSnapshot);
      const cleanMedia = removeUndefined(session.progressMedia || []);

      const completeWorkoutSessionFn = httpsCallable<
        {
          sessionId: string;
          workoutSnapshot: WorkoutSession['workoutSnapshot'];
          sessionNote: string;
          progressMedia: WorkoutSession['progressMedia'];
          rating: number | null;
          preferredUnit: 'kg' | 'lb';
        },
        { success: boolean; sessionId: string; alreadyCompleted?: boolean }
      >(functions, 'completeWorkoutSession');

      await completeWorkoutSessionFn({
        sessionId,
        workoutSnapshot: cleanSnapshot,
        sessionNote: sessionNote || '',
        progressMedia: cleanMedia,
        rating: sessionRating || null,
        preferredUnit,
      });

      console.log('[SessionView] Session completed successfully');
      await schedulePostWorkoutCelebration(sessionId);
      
      // Show confetti, then let user view summary (no auto-redirect)
      setTimeout(() => {
        setShowConfetti(false);
      }, 3000);
    } catch (err: any) {
      console.error('[SessionView] Error completing session:', err);
      console.error('[SessionView] Error details:', err.code, err.message);
      setError(`Session error: ${err.message || 'Failed to complete session'}`);
      setShowConfetti(false);
    } finally {
      setSaving(false);
    }
  };

  // Add comment
  const addComment = async () => {
    if (!session || !user || !userData || !commentText.trim()) return;

    await addDoc(collection(db, 'comments'), {
      sessionId,
      trainerId: session.trainerId,
      clientId: session.clientId,
      workoutItemId: null,
      authorUserId: user.uid,
      authorRole: userData.role,
      text: commentText.trim(),
      createdAt: serverTimestamp(),
    });

    setCommentText('');
  };

  // Skip exercise
  const handleSkipExercise = async (itemId: string, reason?: string) => {
    if (!session) return;

    const updatedItems = session.workoutSnapshot.items.map((item) => {
      if (item.id !== itemId) return item;
      // Build updated item - only include skipReason if provided
      const updated = { ...item, skipped: true };
      if (reason) {
        updated.skipReason = reason;
      }
      return updated;
    });

    await updateSession({
      ...session,
      workoutSnapshot: { ...session.workoutSnapshot, items: updatedItems },
    });
  };

  // Exit the session view without changing session state so it can be resumed.
  const leaveSession = () => {
    setShowCancelDialog(false);
    navigate('/client');
  };

  const abandonSession = async () => {
    if (!sessionId || !session) return;

    setSaving(true);
    setError(null);

    try {
      const abandonWorkoutSessionFn = httpsCallable<
        { sessionId: string },
        { success: boolean; sessionId: string; alreadyAbandoned?: boolean }
      >(functions, 'abandonWorkoutSession');

      await abandonWorkoutSessionFn({ sessionId });

      setShowCancelDialog(false);
      navigate('/client');
    } catch (err) {
      console.error('Failed to abandon session', err);
      setError('Unable to abandon this session right now.');
    } finally {
      setSaving(false);
    }
  };

  const navigateBackFromSummary = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate('/client');
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card variant="elevated" padding="lg" className="max-w-md w-full text-center">
          <p className="text-danger-600 dark:text-danger-400 mb-4">{error}</p>
          <Button variant="ghost" onClick={() => navigate('/client')}>
            <CaretLeft size={16} weight="bold" />
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  if (!session || cards.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="flex items-center gap-3">
          <svg
            className="animate-spin h-5 w-5 text-primary-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p className="text-gray-700 dark:text-slate-200">Loading session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col safe-bottom" data-testid="session-view">
      {showConfetti && <Confetti active={showConfetti} />}

      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-white/10 bg-white dark:bg-slate-900 safe-top">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            {session.status !== 'in_progress' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={navigateBackFromSummary}
                className="mb-2"
              >
                <CaretLeft size={16} weight="bold" />
                Back to Dashboard
              </Button>
            )}
            <h1 className="text-xl font-bold text-gray-900 dark:text-white truncate">
              {session.workoutSnapshot.name}
            </h1>
          </div>

          <div className="flex items-center gap-2">
            {!isCompletionScreen && (
              <button
                onClick={() => setPlaylistOpen(true)}
                className="p-2 rounded-lg transition bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-white/20"
                title="View Playlist"
                data-testid="session-playlist-button"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            )}

            {session.status === 'in_progress' && !isCompletionScreen && (
              <Button variant="success" size="sm" onClick={completeSession} isLoading={saving} data-testid="finish-session-button">
                {saving ? 'Saving...' : 'Finish'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      {session.status !== 'in_progress' ? (
        <main className="flex-1 p-6 overflow-y-auto">
          <div className="max-w-3xl mx-auto">
            <SessionSummary session={session} />
          </div>
        </main>
      ) : !isCompletionScreen ? (
        <div className="flex-1 flex flex-col">
          <CardNavigator
            cards={cards}
            currentIndex={currentCardIndex}
            isPlaying={isPlaying}
            ttsEnabled={ttsEnabled}
            restTimerStates={restTimerStates}
            onNavigate={setCurrentCardIndex}
            onTogglePlay={handleTogglePlay}
            onToggleTts={handleToggleTts}
            onSetUpdate={handleSetUpdate}
            onRestTimeUpdate={(cardId, seconds) =>
              setRestTimerStates((prev) => ({ ...prev, [cardId]: seconds }))
            }
            onSkipExercise={handleSkipExercise}
            onOpenSwap={openSwapModal}
            onCancel={() => setShowCancelDialog(true)}
          />
        </div>
      ) : (
        <CompletionScreen
          session={session}
          sessionRating={sessionRating}
          onSessionRatingChange={setSessionRating}
          sessionNote={sessionNote}
          onSessionNoteChange={setSessionNote}
          comments={comments}
          commentText={commentText}
          onCommentTextChange={setCommentText}
          onAddComment={addComment}
          onBack={() => setCurrentCardIndex(cards.length - 1)}
          onFinish={completeSession}
          saving={saving}
        />
      )}

      <ExerciseSwapModal
        isOpen={!!swapItem}
        currentExerciseName={swapItem?.exerciseName || 'Exercise'}
        targetMuscleGroups={swapItem?.targetMuscleGroups}
        options={swapOptions}
        loading={swapLoading}
        saving={swapSaving}
        error={swapError}
        onClose={closeSwapModal}
        onSwap={handleExerciseSwap}
      />

      {/* Playlist View */}
      <PlaylistView
        cards={cards}
        currentIndex={currentCardIndex}
        isOpen={playlistOpen}
        onClose={() => setPlaylistOpen(false)}
        onNavigate={(index: number) => {
          setCurrentCardIndex(index);
          setPlaylistOpen(false);
        }}
        isMobile={isMobile}
      />

      {/* Cancel Confirmation Dialog */}
      {showCancelDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <Card padding="lg" variant="elevated" className="max-w-sm w-full space-y-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                Leave workout?
              </h3>
              <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
                Leave keeps this session resumable. Abandon session ends it without counting it as completed.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button
                variant="ghost"
                className="flex-1"
                onClick={() => setShowCancelDialog(false)}
              >
                Keep going
              </Button>
              <Button
                variant="secondary"
                className="flex-1"
                onClick={leaveSession}
              >
                Leave workout
              </Button>
              <Button
                variant="danger"
                className="flex-1"
                onClick={abandonSession}
                disabled={saving}
              >
                Abandon session
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
